import { useEffect, useMemo, useState } from "react";
import { AndroidOutlined, AppleOutlined, CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Typography, message } from "antd";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Text, Title } = Typography;

const IOS_STORE_URL =
  process.env.REACT_APP_OLITASK_IOS_DOWNLOAD_URL ||
  "https://apps.apple.com/us/app/olitask/id6756482938";
const ANDROID_STORE_URL =
  process.env.REACT_APP_OLITASK_ANDROID_DOWNLOAD_URL ||
  "https://play.google.com/store/apps/details?id=com.olitask.app";

const isIosDevice = () => {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.platform || "";
  const userAgent = navigator.userAgent || "";
  const touchPoints = navigator.maxTouchPoints || 0;
  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && touchPoints > 1);
};

const isAndroidDevice = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

const getDevicePlatform = () => {
  if (isIosDevice()) return "ios";
  if (isAndroidDevice()) return "android";
  return "desktop";
};

const downloadOptions = [
  {
    key: "ios",
    title: "Download for iOS",
    caption: "Open Olitask on iPhone or iPad from the App Store.",
    url: IOS_STORE_URL,
    icon: <AppleOutlined />,
  },
  {
    key: "android",
    title: "Download for Android",
    caption: "Open Olitask on Android from Google Play.",
    url: ANDROID_STORE_URL,
    icon: <AndroidOutlined />,
  },
];

const AppDownloadPage = () => {
  const [redirecting, setRedirecting] = useState(false);
  const platform = useMemo(getDevicePlatform, []);

  useEffect(() => {
    if (platform === "desktop") return undefined;
    const target = platform === "ios" ? IOS_STORE_URL : ANDROID_STORE_URL;
    setRedirecting(true);
    const timeout = window.setTimeout(() => {
      window.location.replace(target);
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [platform]);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("Download link copied.");
    } catch {
      message.error("Unable to copy link.");
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background:
          "linear-gradient(135deg, rgba(247,255,251,1) 0%, rgba(238,248,255,1) 100%)",
      }}
    >
      <section
        style={{
          width: "min(960px, 100%)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
          gap: 24,
          alignItems: "center",
          border: "1px solid rgba(0, 128, 255, 0.16)",
          borderRadius: 24,
          padding: 24,
          background: "rgba(255,255,255,0.94)",
          boxShadow: "0 28px 76px rgba(0, 56, 86, 0.16)",
        }}
      >
        <img
          src="/app-download.jpg"
          alt="Download Olitask app"
          style={{
            width: "100%",
            borderRadius: 20,
            objectFit: "cover",
            boxShadow: "0 18px 44px rgba(0, 56, 86, 0.14)",
          }}
        />

        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <span
            style={{
              display: "inline-flex",
              width: 56,
              height: 56,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 18,
              color: OLIVEEB_COLORS.white,
              background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebBlue} 0%, ${OLIVEEB_COLORS.oliveebGreen} 100%)`,
              fontSize: 24,
            }}
          >
            <DownloadOutlined />
          </span>
          <div>
            <Title level={1} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebGreen }}>
              Download Olitask
            </Title>
            <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 16 }}>
              {redirecting
                ? "Opening the right store for your device..."
                : "Choose your store link or copy it to share with another device."}
            </Text>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {downloadOptions.map((option) => (
              <div
                key={option.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))",
                  gap: 10,
                  alignItems: "center",
                  padding: 12,
                  border: "1px solid rgba(0, 128, 255, 0.16)",
                  borderRadius: 16,
                  background: "linear-gradient(135deg, #FFFFFF 0%, #F4FBFF 100%)",
                }}
              >
                <span>
                  <Text style={{ display: "block", color: OLIVEEB_COLORS.oliveebDarkBlue, fontWeight: 900 }}>
                    {option.icon} {option.title}
                  </Text>
                  <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
                    {option.caption}
                  </Text>
                </span>
                <Button
                  type="primary"
                  href={option.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    borderRadius: 999,
                    background: OLIVEEB_COLORS.oliveebGreen,
                    borderColor: OLIVEEB_COLORS.oliveebGreen,
                    fontWeight: 900,
                  }}
                >
                  Open
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  aria-label={`Copy ${option.title} link`}
                  onClick={() => copyLink(option.url)}
                  style={{ borderRadius: 999, color: OLIVEEB_COLORS.oliveebBlue }}
                />
              </div>
            ))}
          </div>
        </Space>
      </section>
    </main>
  );
};

export default AppDownloadPage;
